import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MessageSquare,
  Star,
  Loader2,
  ChevronLeft,
  ChevronRight,
  X,
  ImageIcon,
  AtSign,
} from "lucide-react";
import { Pagination } from "@/components/ui/pagination";
import {
  DeviceIconWithLabel,
  OsIconWithLabel,
} from "@/components/feedback/BugReportDeviceOsIcons";
import type {
  BugReportDeviceType,
  BugReportOs,
} from "@/components/feedback/BugReportDeviceOsIcons";
import {
  useAdminFeedbackList,
  useAdminFeedbackDetail,
  useAdminBugReportDetail,
} from "@/hooks/useAdminFeedback";
import { useAdmin } from "@/hooks/useAdmin";
import type { BugReportStatus } from "@/services/feedback/adminFeedbackService";
import type { AdminForMention } from "@/services/admin/adminService";
import { toast } from "sonner";
import { format } from "date-fns";
import { sv, enUS } from "date-fns/locale";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n/labels/adminLabels";
import { usePersistedPageSize } from "@/hooks/usePersistedPageSize";

const PAGE_SIZES = [10, 25, 50];

function getStatusLabels(labels: {
  statusNew: string;
  statusOngoing: string;
  statusSolved: string;
  statusRejected: string;
}): Record<BugReportStatus, string> {
  return {
    NEW: labels.statusNew,
    ONGOING: labels.statusOngoing,
    SOLVED: labels.statusSolved,
    REJECTED: labels.statusRejected,
  };
}

/** Färger per status: Ny = blå, Pågående = gul, Löst = grön, Avvisad = röd */
const STATUS_COLORS: Record<BugReportStatus, string> = {
  NEW: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-800",
  ONGOING:
    "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-800",
  SOLVED:
    "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-200 dark:border-green-800",
  REJECTED:
    "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-200 dark:border-red-800",
};

function truncate(str: string, len: number) {
  if (str.length <= len) return str;
  return str.slice(0, len) + "...";
}

export default function AdminFeedback() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const adminLocale = adminLabels(language) as {
    feedbackAdmin?: Record<string, unknown>;
  };
  const fb = (adminLocale.feedbackAdmin || {}) as Record<string, string> & {
    device?: Record<string, string>;
    os?: Record<string, string>;
  };
  const STATUS_LABELS = getStatusLabels({
    statusNew: fb.statusNew ?? "Ny",
    statusOngoing: fb.statusOngoing ?? "Pågående",
    statusSolved: fb.statusSolved ?? "Löst",
    statusRejected: fb.statusRejected ?? "Avvisad",
  });
  const dateLocale = language === "en" ? enUS : sv;
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<"feedback" | "bugs">("feedback");

  const [feedbackPage, setFeedbackPage] = useState(1);
  const [feedbackLimit, setFeedbackLimit] = usePersistedPageSize(
    "pageSize:adminFeedback",
    10,
    PAGE_SIZES,
  );
  const [bugPage, setBugPage] = useState(1);
  const [bugLimit, setBugLimit] = usePersistedPageSize(
    "pageSize:adminBugs",
    10,
    PAGE_SIZES,
  );
  const [bugStatusFilter, setBugStatusFilter] = useState<
    BugReportStatus | "ALL"
  >("ALL");

  const [selectedFeedbackId, setSelectedFeedbackId] = useState<string | null>(
    null,
  );
  const [selectedBugId, setSelectedBugId] = useState<string | null>(null);
  const [bugCommentText, setBugCommentText] = useState("");
  const [bugCommentMentions, setBugCommentMentions] = useState<string[]>([]);
  const [showBugMentions, setShowBugMentions] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState<
    { previewUrl: string; fullSizeUrl: string }[]
  >([]);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const {
    feedbackList,
    isLoadingFeedbackList,
    bugReportList,
    isLoadingBugReportList,
  } = useAdminFeedbackList(
    feedbackPage,
    feedbackLimit,
    bugPage,
    bugLimit,
    true,
    true,
    bugStatusFilter,
  );

  const { feedbackDetail, isLoadingFeedbackDetail } = useAdminFeedbackDetail(
    selectedFeedbackId,
    true,
  );

  const {
    bugReportDetail,
    isLoadingBugReportDetail,
    updateStatus,
    isUpdatingStatus,
    addComment,
    isAddingComment,
  } = useAdminBugReportDetail(selectedBugId, true);

  const { adminsForMentions } = useAdmin();
  const bugMentionColleagues = adminsForMentions.data ?? [];
  const isLoadingBugMentions = adminsForMentions.isLoading;

  // Open bug report from URL (e.g. notification link: ?tab=bugs&id=xxx)
  useEffect(() => {
    const tab = searchParams.get("tab");
    const id = searchParams.get("id");
    if (tab === "bugs" && id) {
      setActiveTab("bugs");
      setSelectedBugId(id);
    }
  }, [searchParams]);

  const handleOpenFeedback = (id: string) => {
    setSelectedFeedbackId(id);
  };

  const handleOpenBug = (id: string) => {
    setSelectedBugId(id);
  };

  const handleBugStatusChange = async (id: string, status: BugReportStatus) => {
    try {
      await updateStatus({ id, status });
      toast.success(fb.statusUpdated ?? "Status uppdaterad");
    } catch {
      toast.error(fb.statusUpdateError ?? "Kunde inte uppdatera status");
    }
  };

  const handleAddBugComment = async () => {
    if (!selectedBugId || !bugCommentText.trim()) return;
    const mentionsToSend =
      bugCommentMentions.length > 0 ? bugCommentMentions : undefined;
    try {
      await addComment({
        id: selectedBugId,
        text: bugCommentText.trim(),
        mentions: mentionsToSend,
      });
      const mentionedNames =
        mentionsToSend?.length &&
        mentionsToSend
          .map(
            (actorId) =>
              bugMentionColleagues.find((c) => c.id === actorId)?.name,
          )
          .filter(Boolean)
          .join(", ");
      setBugCommentText("");
      setBugCommentMentions([]);
      toast.success(
        fb.commentAdded ?? "Kommentar tillagd",
        mentionedNames
          ? {
              description: `${mentionedNames} ${fb.commentAddedNotify ?? "har notifierats."}`,
            }
          : undefined,
      );
    } catch {
      toast.error(fb.commentAddError ?? "Kunde inte lägga till kommentar");
    }
  };

  const insertBugMention = (colleague: AdminForMention) => {
    setBugCommentText((prev) => `${prev}@${colleague.name} `);
    setBugCommentMentions((prev) =>
      prev.includes(colleague.id) ? prev : [...prev, colleague.id],
    );
    setShowBugMentions(false);
  };

  const openGallery = (
    media: { previewUrl: string; fullSizeUrl: string }[],
    index: number,
  ) => {
    setGalleryImages(media);
    setGalleryIndex(index);
    setGalleryOpen(true);
  };

  useEffect(() => {
    if (!galleryOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        setGalleryIndex((i) => Math.max(0, i - 1));
        e.preventDefault();
      } else if (e.key === "ArrowRight") {
        setGalleryIndex((i) => Math.min(galleryImages.length - 1, i + 1));
        e.preventDefault();
      } else if (e.key === "Escape") {
        setGalleryOpen(false);
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [galleryOpen, galleryImages.length]);

  const feedbackData = activeTab === "feedback" ? feedbackList : null;
  const bugData = activeTab === "bugs" ? bugReportList : null;

  return (
    <div className="container max-w-5xl py-8">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/dashboard")}
          aria-label={fb.back ?? "Tillbaka"}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-semibold">
          {fb.title ?? "Feedback & felrapporter"}
        </h1>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as "feedback" | "bugs")}
      >
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="feedback">
            {fb.tabFeedback ?? "Feedback"}
          </TabsTrigger>
          <TabsTrigger value="bugs">{fb.tabBugs ?? "Fel/Buggar"}</TabsTrigger>
        </TabsList>

        <TabsContent value="feedback" className="mt-6 space-y-4">
          {feedbackList?.averageStars != null && (
            <div className="pl-2 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-muted-foreground">
                  {fb.averageRating ?? "Genomsnittsbetyg:"}
                </span>
                <span className="font-medium tabular-nums">
                  {feedbackList.averageStars.toFixed(1)}
                </span>
                <div
                  className="flex items-center gap-0.5"
                  aria-label={`${feedbackList.averageStars.toFixed(1)} ${fb.starsOf5 ?? "av 5 stjärnor"}`}
                >
                  {[0, 1, 2, 3, 4].map((i) => {
                    const avg = feedbackList.averageStars!;
                    const fill = Math.max(0, Math.min(1, avg - i));
                    return (
                      <div key={i} className="relative h-5 w-5 shrink-0">
                        <Star
                          className="h-5 w-5 text-amber-400/30"
                          strokeWidth={1.5}
                          aria-hidden
                        />
                        {fill > 0 && (
                          <div
                            className="absolute inset-0 overflow-hidden"
                            style={{ width: `${fill * 100}%` }}
                          >
                            <Star
                              className="h-5 w-5 fill-amber-400 text-amber-400"
                              strokeWidth={1.5}
                              aria-hidden
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {isLoadingFeedbackList ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : feedbackData?.items.length ? (
            <>
              <div className="grid gap-3">
                {feedbackData.items.map((item) => (
                  <Card
                    key={item.id}
                    className={`cursor-pointer transition-colors hover:bg-muted/50 ${!item.read ? "border-l-4 border-l-amber-500" : ""}`}
                    onClick={() => handleOpenFeedback(item.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-0.5">
                          <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                          {item.stars}
                        </span>
                        <span>•</span>
                        <span>
                          {item.imageCount} {fb.images ?? "bilder"}
                        </span>
                        {item.submittedBy?.email && (
                          <>
                            <span>•</span>
                            <span>
                              {fb.by ?? "Av:"} {item.submittedBy.email}
                            </span>
                          </>
                        )}
                      </div>
                      {item.description != null && (
                        <p className="text-sm text-foreground mt-2 line-clamp-2">
                          {truncate(item.description, 100)}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
              <Pagination
                currentPage={feedbackData.page}
                totalPages={feedbackData.totalPages}
                totalItems={feedbackData.total}
                itemsPerPage={feedbackLimit}
                onPageChange={setFeedbackPage}
                onItemsPerPageChange={(n) => {
                  setFeedbackLimit(n);
                  setFeedbackPage(1);
                }}
                itemsPerPageOptions={PAGE_SIZES}
              />
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                {fb.noFeedbackYet ?? "Inga feedback ännu."}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="bugs" className="mt-6 space-y-4">
          <Tabs
            value={bugStatusFilter}
            onValueChange={(v) => {
              setBugStatusFilter(v as BugReportStatus | "ALL");
              setBugPage(1);
            }}
          >
            <TabsList className="inline-flex h-9 w-full max-w-md">
              <TabsTrigger
                value="ALL"
                className="flex-1 data-[state=active]:bg-muted data-[state=active]:text-foreground"
              >
                {fb.all ?? "Alla"}
              </TabsTrigger>
              <TabsTrigger
                value="NEW"
                className="flex-1 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-800 dark:data-[state=active]:bg-blue-900/50 dark:data-[state=active]:text-blue-200"
              >
                {STATUS_LABELS.NEW}
              </TabsTrigger>
              <TabsTrigger
                value="ONGOING"
                className="flex-1 data-[state=active]:bg-amber-100 data-[state=active]:text-amber-800 dark:data-[state=active]:bg-amber-900/50 dark:data-[state=active]:text-amber-200"
              >
                {STATUS_LABELS.ONGOING}
              </TabsTrigger>
              <TabsTrigger
                value="SOLVED"
                className="flex-1 data-[state=active]:bg-green-100 data-[state=active]:text-green-800 dark:data-[state=active]:bg-green-900/50 dark:data-[state=active]:text-green-200"
              >
                {STATUS_LABELS.SOLVED}
              </TabsTrigger>
              <TabsTrigger
                value="REJECTED"
                className="flex-1 data-[state=active]:bg-red-100 data-[state=active]:text-red-800 dark:data-[state=active]:bg-red-900/50 dark:data-[state=active]:text-red-200"
              >
                {STATUS_LABELS.REJECTED}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {isLoadingBugReportList ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : bugData?.items.length ? (
            <>
              <div className="grid gap-3">
                {bugData.items.map((item) => (
                  <Card
                    key={item.id}
                    className={`cursor-pointer transition-colors hover:bg-muted/50 ${!item.read ? "border-l-4 border-l-amber-500" : ""}`}
                    onClick={() => handleOpenBug(item.id)}
                  >
                    <CardContent className="p-4">
                      <p className="font-medium truncate text-foreground">
                        {item.title}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 mt-2 text-sm text-muted-foreground">
                        {/* Tid */}
                        <span>
                          {format(
                            new Date(item.createdAt),
                            "d MMM yyyy, HH:mm",
                            { locale: dateLocale },
                          )}
                        </span>
                        <span className="text-muted-foreground/60">•</span>
                        {/* Enhet */}
                        {item.deviceType && (
                          <>
                            <DeviceIconWithLabel
                              value={item.deviceType as BugReportDeviceType}
                              iconClassName="h-3.5 w-3.5"
                              className="inline-flex"
                              labels={fb.device}
                            />
                            <span className="text-muted-foreground/60">•</span>
                          </>
                        )}
                        {/* Operativsystem */}
                        {item.os && (
                          <>
                            <OsIconWithLabel
                              value={item.os as BugReportOs}
                              iconClassName="h-3.5 w-3.5"
                              className="inline-flex"
                              labels={fb.os}
                            />
                            <span className="text-muted-foreground/60">•</span>
                          </>
                        )}
                        {/* Status – tydlig klickbar väljare */}
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex"
                        >
                          <Select
                            value={item.status}
                            onValueChange={(v) =>
                              handleBugStatusChange(
                                item.id,
                                v as BugReportStatus,
                              )
                            }
                            disabled={isUpdatingStatus}
                          >
                            <SelectTrigger
                              className={`h-8 min-w-[100px] border rounded-md px-2.5 shadow-sm hover:opacity-90 focus:ring-2 focus:ring-ring focus:ring-offset-1 text-xs font-medium [&>svg]:h-3.5 [&>svg]:w-3.5 ${STATUS_COLORS[item.status]}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {(
                                Object.keys(STATUS_LABELS) as BugReportStatus[]
                              ).map((s) => (
                                <SelectItem key={s} value={s}>
                                  {STATUS_LABELS[s]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {item.submittedBy?.email && (
                          <>
                            <span className="text-muted-foreground/60">•</span>
                            <span className="truncate max-w-[180px]">
                              {fb.by ?? "Av:"} {item.submittedBy.email}
                            </span>
                          </>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground/80 mt-2">
                        {item.imageCount} {fb.attachments ?? "bilagor"}
                      </p>
                      {item.description != null && (
                        <p className="text-sm text-foreground/90 mt-1 line-clamp-2">
                          {truncate(item.description, 100)}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
              <Pagination
                currentPage={bugData.page}
                totalPages={bugData.totalPages}
                totalItems={bugData.total}
                itemsPerPage={bugLimit}
                onPageChange={setBugPage}
                onItemsPerPageChange={(n) => {
                  setBugLimit(n);
                  setBugPage(1);
                }}
                itemsPerPageOptions={PAGE_SIZES}
              />
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                {fb.noBugReportsYet ?? "Inga felrapporter ännu."}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <Dialog
        open={!!selectedFeedbackId}
        onOpenChange={(open) => !open && setSelectedFeedbackId(null)}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{feedbackDetail?.title ?? "—"}</DialogTitle>
          </DialogHeader>
          {isLoadingFeedbackDetail ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : feedbackDetail ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                <span>
                  {feedbackDetail.stars} {fb.starsLabel ?? "stjärnor"}
                </span>
                <span className="text-muted-foreground">
                  •{" "}
                  {format(
                    new Date(feedbackDetail.createdAt),
                    "d MMM yyyy, HH:mm",
                    {
                      locale: dateLocale,
                    },
                  )}
                </span>
                {feedbackDetail.submittedBy?.email && (
                  <span className="text-muted-foreground">
                    • {fb.reportedBy ?? "Rapporterad av:"}{" "}
                    {feedbackDetail.submittedBy.email}
                  </span>
                )}
              </div>
              <p className="text-sm whitespace-pre-wrap">
                {feedbackDetail.description}
              </p>
              {feedbackDetail.media?.length ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                    <ImageIcon className="h-4 w-4" />
                    {feedbackDetail.media.length} {fb.attachments ?? "bilagor"}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {feedbackDetail.media.map((m, idx) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() =>
                          openGallery(
                            feedbackDetail.media.map((x) => ({
                              previewUrl: x.previewUrl,
                              fullSizeUrl: x.fullSizeUrl,
                            })),
                            idx,
                          )
                        }
                        className="rounded-lg overflow-hidden border border-border hover:ring-2 hover:ring-primary/50 focus:outline-none focus:ring-2 focus:ring-primary shrink-0"
                      >
                        <img
                          src={m.previewUrl}
                          alt={m.originalName}
                          className="h-20 w-20 object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!selectedBugId}
        onOpenChange={(open) => !open && setSelectedBugId(null)}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{fb.bugReportTitle ?? "Felrapport"}</DialogTitle>
          </DialogHeader>
          {isLoadingBugReportDetail ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : bugReportDetail ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <span>
                  {format(
                    new Date(bugReportDetail.occurredAt),
                    "d MMM yyyy, HH:mm",
                    {
                      locale: dateLocale,
                    },
                  )}
                </span>
                <span>•</span>
                {bugReportDetail.deviceType && (
                  <DeviceIconWithLabel
                    value={bugReportDetail.deviceType as BugReportDeviceType}
                    iconClassName="h-4 w-4"
                    labels={fb.device}
                  />
                )}
                <span>•</span>
                {bugReportDetail.os && (
                  <OsIconWithLabel
                    value={bugReportDetail.os as BugReportOs}
                    iconClassName="h-4 w-4"
                    labels={fb.os}
                  />
                )}
                {bugReportDetail.submittedBy?.email && (
                  <>
                    <span>•</span>
                    <span>
                      {fb.reportedBy ?? "Rapporterad av:"}{" "}
                      {bugReportDetail.submittedBy.email}
                    </span>
                  </>
                )}
              </div>
              <div>
                <Label>Status</Label>
                <Select
                  value={bugReportDetail.status}
                  onValueChange={(v) =>
                    handleBugStatusChange(
                      bugReportDetail.id,
                      v as BugReportStatus,
                    )
                  }
                  disabled={isUpdatingStatus}
                >
                  <SelectTrigger
                    className={`mt-1 border font-medium ${STATUS_COLORS[bugReportDetail.status]}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(STATUS_LABELS) as BugReportStatus[]).map(
                      (s) => (
                        <SelectItem key={s} value={s}>
                          {STATUS_LABELS[s]}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-sm whitespace-pre-wrap">
                {bugReportDetail.description}
              </p>
              {bugReportDetail.media?.length ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                    <ImageIcon className="h-4 w-4" />
                    {bugReportDetail.media.length} {fb.attachments ?? "bilagor"}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {bugReportDetail.media.map((m, idx) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() =>
                          openGallery(
                            bugReportDetail.media!.map((x) => ({
                              previewUrl: x.previewUrl,
                              fullSizeUrl: x.fullSizeUrl,
                            })),
                            idx,
                          )
                        }
                        className="rounded-lg overflow-hidden border border-border hover:ring-2 hover:ring-primary/50 focus:outline-none focus:ring-2 focus:ring-primary shrink-0"
                      >
                        <img
                          src={m.previewUrl}
                          alt={m.originalName}
                          className="h-20 w-20 object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              <Card>
                <CardContent className="pt-4 space-y-4">
                  <p className="font-medium flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    {fb.comments ?? "Kommentarer"}
                  </p>
                  {bugReportDetail.comments?.length ? (
                    <ul className="space-y-2">
                      {bugReportDetail.comments.map((c) => (
                        <li
                          key={c.id}
                          className="text-sm p-2 rounded bg-muted/50 border-l-2 border-primary"
                        >
                          <p className="whitespace-pre-wrap">{c.text}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {c.admin?.displayName || c.admin?.email
                              ? `${c.admin.displayName || c.admin.email} • `
                              : null}
                            {format(
                              new Date(c.createdAt),
                              "d MMM yyyy, HH:mm",
                              {
                                locale: dateLocale,
                              },
                            )}
                          </p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {fb.noCommentsYet ?? "Inga kommentarer än."}
                    </p>
                  )}
                  <div className="space-y-2">
                    <div className="relative">
                      <Textarea
                        placeholder={
                          fb.addCommentPlaceholder ??
                          "Add comment... (type @ to tag colleagues)"
                        }
                        value={bugCommentText}
                        onChange={(e) => setBugCommentText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "@") setShowBugMentions(true);
                        }}
                        rows={2}
                        className="flex-1 min-w-0"
                      />
                      {showBugMentions && (
                        <div className="absolute bottom-full left-0 mt-1 bg-card border rounded-md shadow-lg p-2 space-y-1 w-64 z-10 max-h-64 overflow-y-auto">
                          {isLoadingBugMentions ? (
                            <div className="px-2 py-1 text-sm text-muted-foreground">
                              {fb.loading ?? "Laddar..."}
                            </div>
                          ) : bugMentionColleagues.length === 0 ? (
                            <div className="px-2 py-1 text-sm text-muted-foreground">
                              {fb.noColleaguesAvailable ??
                                "No colleagues available"}
                            </div>
                          ) : (
                            bugMentionColleagues.map((colleague) => (
                              <button
                                key={colleague.id}
                                type="button"
                                onClick={() => insertBugMention(colleague)}
                                className="w-full hover:bg-accent/20 text-left px-2 py-1 rounded text-sm"
                              >
                                {colleague.name}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowBugMentions(!showBugMentions)}
                        className="hover:bg-accent/20"
                        disabled={isLoadingBugMentions}
                      >
                        <AtSign className="h-4 w-4" />
                        <span className="ml-1">
                          {fb.tagColleague ?? "Tagga kollega"}
                        </span>
                        {bugCommentMentions.length > 0 && (
                          <span className="ml-2 bg-primary/20 text-primary rounded-full px-2 py-0.5 text-xs">
                            {bugCommentMentions.length}
                          </span>
                        )}
                      </Button>
                      <Button
                        onClick={handleAddBugComment}
                        disabled={!bugCommentText.trim() || isAddingComment}
                      >
                        {isAddingComment ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          (fb.send ?? "Skicka")
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Fullscreen image gallery - custom close in header, hide default X */}
      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent
          showCloseButton={false}
          className="max-w-[95vw] max-h-[95vh] w-full h-full p-2 flex flex-col bg-black/95 border-none"
        >
          <div className="flex items-center justify-between gap-2 text-white">
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
              disabled={galleryImages.length <= 1 || galleryIndex <= 0}
              onClick={() => setGalleryIndex((i) => Math.max(0, i - 1))}
              aria-label={fb.prevImage ?? "Föregående bild"}
            >
              <ChevronLeft className="h-8 w-8" />
            </Button>
            <span className="text-sm">
              {galleryIndex + 1} / {galleryImages.length}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
              disabled={
                galleryImages.length <= 1 ||
                galleryIndex >= galleryImages.length - 1
              }
              onClick={() =>
                setGalleryIndex((i) =>
                  Math.min(galleryImages.length - 1, i + 1),
                )
              }
              aria-label="Nästa bild"
            >
              <ChevronRight className="h-8 w-8" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20 ml-auto"
              onClick={() => setGalleryOpen(false)}
              aria-label={fb.close ?? "Stäng"}
            >
              <X className="h-6 w-6" />
            </Button>
          </div>
          <div className="flex-1 min-h-0 flex items-center justify-center">
            {galleryImages[galleryIndex] && (
              <img
                src={galleryImages[galleryIndex].fullSizeUrl}
                alt=""
                className="max-w-full max-h-[85vh] object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
